import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import { type FC } from 'react';
import {
  FunnelStatusTerminalOutcome,
  type FunnelStatus,
} from 'src/api/generated';
import { PiArrowsOutLineHorizontal } from 'react-icons/pi';
import { AiBadge } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import { CountBadge } from 'src/modules/shared/components/atoms/count-badge';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';

interface CollapsedStatusRailProps {
  status: FunnelStatus;
  count: number;
  countLoading?: boolean;
  isAiLinked: boolean;
  isHighlighted: boolean;
  className?: string;
  onExpand?: () => void;
}

const collapsedRailInnerClasses =
  'flex h-full flex-col items-center rounded-xl border px-2 py-3 shadow-sm transition-colors';

export const CollapsedStatusRail: FC<CollapsedStatusRailProps> = ({
  status,
  count,
  countLoading = false,
  isAiLinked,
  isHighlighted,
  className = '',
  onExpand,
}) => {
  const { t } = useI18n();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onExpand}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        onExpand?.();
      }}
      aria-label={t('communications.pipeline.actions.expandColumn')}
      data-collapsed-column="true"
      className={classes(
        collapsedRailInnerClasses,
        '!h-full w-full',
        'cursor-pointer bg-secondary-50/60 hover:bg-secondary-100/70',
        isHighlighted
          ? 'border-primary-300 bg-primary-50/90 hover:bg-primary-100/90'
          : status.terminalOutcome === FunnelStatusTerminalOutcome.POSITIVE
            ? 'border-success-300 bg-success-50 hover:bg-success-100'
            : status.terminalOutcome === FunnelStatusTerminalOutcome.NEGATIVE
              ? 'border-secondary-300 bg-secondary-200/60 hover:bg-secondary-200'
              : 'border-secondary-200/80',
        className
      )}
    >
      <div className="flex flex-col items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-md text-secondary-500">
          <PiArrowsOutLineHorizontal className="h-[18px] w-[18px] text-secondary-500" />
        </div>

        <div className="flex h-6 w-6 shrink-0 items-center justify-center">
          {isAiLinked ? (
            <AiBadge size="md" appearance="filled" variant="neutral" />
          ) : (
            <div className="w-2 h-2 rounded-full bg-secondary-300" />
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-2 py-4">
        <CountBadge
          count={count}
          loading={countLoading}
          size="md"
          variant="secondary"
        />

        <FunnelStatusName
          name={status.name}
          outcome={status.terminalOutcome}
          icon="archive"
          showArchiveTooltip
          weight="semibold"
          className={classes(
            'text-center tracking-tight [writing-mode:vertical-rl] rotate-180 line-clamp-none',
            isAiLinked ? 'text-indigo-950' : 'text-secondary-700'
          )}
        />
      </div>
    </div>
  );
};
